/* Today's target for one exercise card, as the Today tab shows it: the
 * source badge, the set shape, "Use target" and clearing a prescription,
 * plus the rep-range edits of a plan exercise. The maths lives in
 * logic/progression; this only shapes it for the UI (targets.test.js). */
import { programTargets, targetsFor } from './logic/progression';
import { trainingLogs } from './logic/deload';

const isNum = v => typeof v === 'number' && Number.isFinite(v);
const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);
const posInt = v => (isNum(v) && v >= 1 ? Math.floor(v) : null);

export const RULES = ['double', 'linear', 'manual'];
export const RULE_LABEL = { double: 'Double', linear: 'Linear', manual: 'Manual' };

/** A plan exercise's progression rule, with the contract's default. */
export function progressionOf(pe) {
  if (RULES.includes(pe?.progression)) return pe.progression;
  return posInt(pe?.repMin) && posInt(pe?.repMax) ? 'double' : 'linear';
}

/** '8–12' for a rep range, else the plain reps ('10'). */
export function repRangeLabel(pe) {
  const lo = posInt(pe?.repMin);
  const hi = posInt(pe?.repMax);
  if (lo && hi && lo !== hi) return `${Math.min(lo, hi)}–${Math.max(lo, hi)}`;
  return String(posInt(pe?.reps) || hi || lo || '');
}

export function fmtKg(w) {
  return isNum(w) ? String(Math.round(w * 100) / 100) : '';
}

/** Reps of a target set: '5', or '5+' for AMRAP. */
export function repsText(s) {
  if (!isObj(s) || !isNum(s.reps)) return '?';
  return `${s.reps}${s.amrap ? '+' : ''}`;
}

/**
 * "Week 2 · 3s" -> "W2", "Stage 1 · 5×3+" -> "S1", "Week 1 · Day 2 (6×6)"
 * -> "W1D2"; anything else -> "W<index + 1>".
 */
export function shortWeekLabel(label, weekIndex = 0) {
  const s = typeof label === 'string' ? label : '';
  const head = s.match(/^\s*(week|stage|day)\s*(\d+)/i);
  if (!head) return `W${weekIndex + 1}`;
  const day = s.match(/·\s*day\s*(\d+)/i);
  return `${head[1][0].toUpperCase()}${head[2]}${day ? `D${day[1]}` : ''}`;
}

/**
 * The set shape in one line: "82.5 × 8" (straight sets), "80 × 10/9/9"
 * (same weight), "top 90 × 3+" (a ramp), "× 5/5/5+" (no loads yet).
 * @param {Array<{weight, reps, amrap}>} sets
 */
export function targetShape(sets) {
  const list = (Array.isArray(sets) ? sets : []).filter(isObj);
  if (!list.length) return '';
  const reps = list.map(repsText);
  const sameReps = reps.every(r => r === reps[0]);
  if (list.every(s => !isNum(s.weight))) return `× ${sameReps ? reps[0] : reps.join('/')}`;
  const w = list[0].weight;
  if (list.every(s => s.weight === w)) return `${fmtKg(w)} × ${sameReps ? reps[0] : reps.join('/')}`;
  let top = null;
  list.forEach(s => { if (isNum(s.weight) && (!top || s.weight >= top.weight)) top = s; });
  return `top ${fmtKg(top.weight)} × ${repsText(top)}`;
}

/** True when every set has the same load (one line reads better than chips). */
export function isStraight(sets) {
  const list = (Array.isArray(sets) ? sets : []).filter(isObj);
  return list.length > 0 && list.every(s => s.weight === list[0].weight);
}

/**
 * Everything a Today card shows about its target. Precedence and numbers
 * come from targetsFor; this adds the badge and, for a program, the block
 * and where it stands (week x of y, training max).
 * @param {object} state fitness state
 * @param {string} date YYYY-MM-DD shown on the Today tab
 * @param {string} name the shown (possibly swapped) exercise name
 * @param {object} [pe] the plan exercise; omit for ad-hoc work
 * @returns {{source, label, sets, note, basedOn, blockId, rule: string|null,
 *   block: object|null, week: object|null, badge: string|null, shown: boolean}}
 *   shown = a real target (not just last session's numbers); week =
 *   programTargets(...) for a program
 */
export function cardTarget(state, date, name, pe) {
  const target = targetsFor(state, date, name, pe);
  const out = { ...target, rule: null, block: null, week: null, badge: null, shown: false };
  if (target.source === 'prescription') {
    out.badge = target.label === 'Claude' ? 'CLAUDE' : 'YOU';
  } else if (target.source === 'program') {
    const block = (Array.isArray(state?.programs) ? state.programs : []).find(b => isObj(b) && b.id === target.blockId) || null;
    const week = block ? programTargets(block, trainingLogs(state, null), date, state) : null;
    out.block = block;
    out.week = week;
    const name0 = week ? week.label : (target.label || 'Program').split(' · ')[0];
    out.badge = week && week.weekCount > 1 ? `${name0} · ${shortWeekLabel(week.weekLabel, week.weekIndex)}` : name0;
  } else if (target.source === 'progression') {
    out.rule = progressionOf(pe);
    out.badge = RULE_LABEL[out.rule].toUpperCase();
  }
  if (target.deload && out.badge) out.badge = 'DELOAD';
  out.shown = !!out.badge && target.sets.length > 0;
  return out;
}

/**
 * Today's rows filled from the target, unticked, like Copy. AMRAP rows keep
 * reps empty so their "5+" ghost asks for the real count.
 */
export function fillFromTarget(sets) {
  return (Array.isArray(sets) ? sets : []).filter(isObj).map(s => {
    const row = { done: false };
    if (isNum(s.weight)) row.weight = s.weight;
    if (isNum(s.reps) && !s.amrap) row.reps = s.reps;
    return row;
  });
}

/** Remove prescriptions[date][name]; an emptied date goes too. */
export function clearPrescription(state, date, name) {
  const day = state?.prescriptions?.[date];
  if (!isObj(day) || !(name in day)) return state;
  const { [name]: _gone, ...rest } = day;
  const prescriptions = { ...state.prescriptions };
  if (Object.keys(rest).length) prescriptions[date] = rest;
  else delete prescriptions[date];
  return { ...state, prescriptions };
}

/**
 * The amber chip for a liftTrend result: 'STALL' for stalled or regressing,
 * 'HOLDING' when the stall is on a cut (goal 'lose fat'). Null otherwise.
 */
export function stallChip(trend) {
  if (!trend || (trend.status !== 'stalled' && trend.status !== 'regressing')) return null;
  return { text: trend.label === 'holding' ? 'HOLDING' : 'STALL', title: trend.sentence || '' };
}

/**
 * Patch for typing one end of a plan exercise's rep range. The other end is
 * the current range, else the plain reps; `reps` follows repMax for old
 * readers. Order and equal ends are sorted out on blur (normaliseRepRange).
 * @param {object} ex plan exercise
 * @param {'min'|'max'} end
 * @param {number} value
 */
export function repRangePatch(ex, end, value) {
  const reps = posInt(ex?.reps) || 10;
  const lo = posInt(ex?.repMin) || reps;
  const hi = posInt(ex?.repMax) || reps;
  const v = posInt(value);
  if (!v) return {};
  return end === 'min' ? { repMin: v, repMax: hi, reps: hi } : { repMin: lo, repMax: v, reps: v };
}

/**
 * Patch that tidies a rep range after editing one end. An end typed past
 * the other one drags it along (min 12 over max 10 means 12, not 10-12),
 * and equal ends become plain reps (fixed reps, so linear by default).
 * Null removes a field. {} when there is nothing to fix.
 * @param {object} ex plan exercise
 * @param {'min'|'max'} [end] the end just edited; without it ends are swapped
 */
export function normaliseRepRange(ex, end) {
  let lo = posInt(ex?.repMin);
  let hi = posInt(ex?.repMax);
  if (!lo || !hi) return lo || hi ? { reps: lo || hi, repMin: null, repMax: null } : {};
  if (lo > hi) {
    if (end === 'min') hi = lo;
    else if (end === 'max') lo = hi;
    else [lo, hi] = [hi, lo];
  }
  if (lo === hi) return { reps: lo, repMin: null, repMax: null };
  if (lo !== ex.repMin || hi !== ex.repMax || ex.reps !== hi) return { repMin: lo, repMax: hi, reps: hi };
  return {};
}
